import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import ButtonBase from "@mui/material/ButtonBase";
import { alpha } from "@mui/material/styles";
import { DateTime } from "luxon";

import { CARD_SX, TIME_BUCKETS, bucketIdOf } from "/imports/ui/components/booking";

const GRID_COLS = "4rem repeat(7, minmax(0, 1fr))";

function CourtWeekGrid({ court }) {
  const { currency, weekDates, weekGrouped, handleWeekChipClick, getOptionsLabel, todayISO } = court;

  const packMode = court.pack?.active;

  const renderWeekChip = (group, date) => {
    const isMulti = group.slots.length > 1;
    const cur = (group.currency || currency || "").toUpperCase();
    const optionsLabel = getOptionsLabel(group.slots);
    const picked = packMode && court.pack.isPicked(date, group.time);
    return (
      <ButtonBase
        key={group.time}
        onClick={(e) => handleWeekChipClick(e, group, date)}
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          px: 1,
          py: 0.6,
          borderRadius: 1.5,
          border: picked ? "1.5px solid" : "1px solid",
          borderColor: picked ? "primary.main" : "divider",
          bgcolor: picked ? (theme) => alpha(theme.palette.primary.main, 0.08) : "#fff",
          transition: "all 0.12s ease",
          "&:hover": { borderColor: "primary.main", bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06) }
        }}
      >
        <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, lineHeight: 1.15, color: picked ? "primary.main" : "text.primary" }}>
          {group.time}
        </Typography>
        {!packMode && (
          <Typography sx={{ fontSize: "0.62rem", fontWeight: 600, color: "text.secondary", lineHeight: 1.3 }}>
            {isMulti ? "from " : ""}
            {cur} {parseFloat(group.minPrice || 0).toFixed(0)}
          </Typography>
        )}
        <Typography
          sx={{
            fontSize: "0.58rem",
            color: "text.secondary",
            lineHeight: 1.2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "100%"
          }}
        >
          {optionsLabel}
        </Typography>
      </ButtonBase>
    );
  };

  return (
    <Paper elevation={0} sx={{ ...CARD_SX, p: 0, overflow: "hidden" }}>
      {/* Day header row */}
      <Box sx={{ display: "grid", gridTemplateColumns: GRID_COLS, borderBottom: "1px solid", borderColor: "divider" }}>
        <Box />
        {weekDates.map((d) => {
          const dt = DateTime.fromISO(d);
          const isToday = d === todayISO;
          const isPast = d < todayISO;
          return (
            <Box
              key={d}
              sx={{
                textAlign: "center",
                py: 1.25,
                borderLeft: "1px solid",
                borderColor: "divider",
                bgcolor: isToday ? (theme) => alpha(theme.palette.primary.main, 0.1) : "transparent",
                opacity: isPast ? 0.4 : 1
              }}
            >
              <Typography sx={{ fontSize: "0.6rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "text.secondary" }}>
                {dt.toFormat("ccc")}
              </Typography>
              <Typography sx={{ fontSize: "1.05rem", fontWeight: 700, lineHeight: 1.2, color: isToday ? "primary.main" : "text.primary" }}>
                {dt.day}
              </Typography>
              {/* A week can straddle two months, and a column of bare numbers says
                  nothing about which one this is. */}
              <Typography
                sx={{
                  fontSize: "0.55rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  lineHeight: 1,
                  color: isToday ? "primary.main" : "text.secondary",
                  opacity: 0.85
                }}
              >
                {dt.toFormat("LLL")}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* One row per non-empty time-of-day bucket */}
      {TIME_BUCKETS.map((bucket, bi) => {
        const anyInBucket = weekDates.some((d) => (weekGrouped.get(d) || []).some((g) => bucketIdOf(g.time) === bucket.id));
        if (!anyInBucket) return null;
        return (
          <Box
            key={bucket.id}
            sx={{ display: "grid", gridTemplateColumns: GRID_COLS, borderTop: bi > 0 ? "1px solid" : "none", borderColor: "divider" }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "center", pt: 1.5, pb: 1 }}>
              <Typography
                sx={{
                  fontSize: "0.6rem",
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  color: "text.secondary",
                  textAlign: "center"
                }}
              >
                {bucket.label}
              </Typography>
            </Box>
            {weekDates.map((d) => {
              const isPast = d < todayISO;
              const groups = (weekGrouped.get(d) || []).filter((g) => bucketIdOf(g.time) === bucket.id);
              return (
                <Box
                  key={d}
                  sx={{
                    borderLeft: "1px solid",
                    borderColor: "divider",
                    p: 0.75,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.75,
                    minHeight: "3.5rem",
                    bgcolor: d === todayISO ? (theme) => alpha(theme.palette.primary.main, 0.03) : "transparent",
                    opacity: isPast ? 0.4 : 1
                  }}
                >
                  {groups.map((group) => renderWeekChip(group, d))}
                </Box>
              );
            })}
          </Box>
        );
      })}
    </Paper>
  );
}

export default CourtWeekGrid;
